import { useEffect, useState, type ReactNode } from 'react';
import { Globe2, Home, MapPin, RefreshCw } from 'lucide-react';
import CityScene from './scene/CityScene';
import { LOCALES, locations, text, type Locale } from '../shared/content';
import { initialLocale, languageNames } from './i18n';
import './styles.css';

const copy = {
  title: text(
    'Your New York is taking shape.',
    '你的纽约，正在成形。',
    '你的紐約，正在成形。',
    'Tu Nueva York está tomando forma.',
  ),
  status: text(
    'Online game backend is not connected',
    '在线游戏后端尚未连接',
    '線上遊戲後端尚未連線',
    'El servidor del juego aún no está conectado',
  ),
  detail: text(
    'Explore the 3D neighborhood preview. Accounts, saved progress and multiplayer require the game server to be connected.',
    '你可以浏览 3D 街区预览。账号、存档与多人游戏需要连接游戏服务器后才能使用。',
    '你可以瀏覽 3D 街區預覽。帳號、存檔與多人遊戲需要連接遊戲伺服器後才能使用。',
    'Explora la vista previa 3D del barrio. Las cuentas, las partidas guardadas y el multijugador necesitan el servidor del juego.',
  ),
  preview: text('NEIGHBORHOOD PREVIEW', '街区画面预览', '街區畫面預覽', 'VISTA PREVIA DEL BARRIO'),
  retry: text('Check connection', '重新检查连接', '重新檢查連線', 'Comprobar conexión'),
  checking: text('Checking connection…', '正在检查连接…', '正在檢查連線…', 'Comprobando conexión…'),
  visit: text('Explore a place', '浏览地点', '瀏覽地點', 'Explora un lugar'),
};

export default function BackendGate({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const [selected, setSelected] = useState('home');
  const [online, setOnline] = useState(false);
  const [checking, setChecking] = useState(true);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (online) return;
    const controller = new AbortController();
    let disposed = false;
    let busy = false;
    const probe = async () => {
      if (busy || document.visibilityState === 'hidden') return;
      busy = true;
      setChecking(true);
      try {
        const response = await fetch('/api/health', {
          cache: 'no-store',
          signal: AbortSignal.any([controller.signal, AbortSignal.timeout(4000)]),
        });
        const health = response.ok ? await response.json() : null;
        if (!disposed && health?.ok === true && health?.service === 'american-dream-online') setOnline(true);
      } catch {
        // An unavailable backend must never look like a working account form.
      } finally {
        busy = false;
        if (!disposed) setChecking(false);
      }
    };
    void probe();
    const timer = setInterval(() => void probe(), 30000);
    const visible = () => {
      if (document.visibilityState === 'visible') void probe();
    };
    document.addEventListener('visibilitychange', visible);
    return () => {
      disposed = true;
      controller.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [attempt, online]);
  if (online) return children;
  const place = locations.find((item) => item.id === selected) || locations[0];
  return (
    <main className="backend-preview">
      <CityScene character={null} locale={locale} selected={selected} onPlace={setSelected} />
      <header className="topbar">
        <a className="brand" href="/" aria-label="American Dream Online home">
          <span className="brand-mark">
            <Home size={20} />
          </span>
          <span>
            AMERICAN DREAM
            <small>
              ONLINE <i /> NEW YORK
            </small>
          </span>
        </a>
        <label className="preview-language">
          <Globe2 size={16} />
          <select
            aria-label="Language"
            value={locale}
            onChange={(event) => {
              const next = event.target.value as Locale;
              localStorage.setItem('ado-locale', next);
              setLocale(next);
            }}
          >
            {LOCALES.map((language) => (
              <option key={language} value={language}>
                {languageNames[language]}
              </option>
            ))}
          </select>
        </label>
      </header>
      <section className="backend-preview-card">
        <div className="section-eyebrow">{copy.preview[locale]}</div>
        <h1>{copy.title[locale]}</h1>
        <div className="backend-state" role="status">
          <strong>{checking ? copy.checking[locale] : copy.status[locale]}</strong>
          <p>{copy.detail[locale]}</p>
        </div>
        <label className="preview-place">
          <MapPin size={16} />
          <select
            aria-label={copy.visit[locale]}
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
          >
            {locations.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name[locale]}
              </option>
            ))}
          </select>
        </label>
        <p className="preview-description">{place.description[locale]}</p>
        <button className="primary" disabled={checking} onClick={() => setAttempt((value) => value + 1)}>
          <RefreshCw size={15} />
          {copy.retry[locale]}
        </button>
      </section>
    </main>
  );
}
